import { styled } from '@mui/material/styles';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { Icon } from '@shared/icons';
import type { Row as TRow, Table } from '@tanstack/react-table';
import type { ReactNode } from 'react';
import { BodyCell } from '../../components/BodyCell';
import { useDataProvider } from '../../DataProviderContext';
import { CountBubble } from '../../internal/CountBubble';
import { getTableColumnDef } from '../../internal/getTableColumnDef';
import type { GroupHeader } from '../../types';

const Cell = styled(BodyCell, { name: 'GroupRow', slot: 'Cell' })(
  ({ theme }) => ({
    backgroundColor: theme.palette.getGrey(20),
    padding: theme.spacing(1, 2),
    cursor: 'pointer',
  })
);

const Content = styled('div', { name: 'GroupRow', slot: 'Content' })(
  ({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
  })
);

const HeaderContainer = styled('div', {
  name: 'GroupRow',
  slot: 'HeaderContainer',
})({
  display: 'flex',
  alignItems: 'center',
});

const isRowSelectionEnabled = (table: Table<unknown> | undefined) =>
  Boolean(table?.options.enableRowSelection);

const getColSpan = (row: TRow<unknown>) => row.getVisibleCells().length;

const getGroupHeader = (
  table: Table<unknown> | undefined,
  row: TRow<unknown>
): GroupHeader<unknown> =>
  getGroupHeaderColumnDef(table, row)?.GroupHeader ?? GroupHeaderContent;

const getGroupHeaderColumnDef = (
  table: Table<unknown> | undefined,
  row: TRow<unknown>
) => {
  const column = table?.getColumn(row.groupingColumnId ?? '');
  return column ? getTableColumnDef(column) : undefined;
};

export interface GroupHeaderContentProps {
  row: TRow<any>;
  actions?: ReactNode;
}

const ActionsContainer = styled('div', {
  name: 'GroupRow',
  slot: 'ActionsContainer',
})(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

export const GroupHeaderContent = ({
  row,
  actions,
}: GroupHeaderContentProps) => (
  <Content>
    <HeaderContainer>
      <Typography variant="button">
        {typeof row.groupingValue === 'string' && row.groupingValue}
      </Typography>
      <CountBubble badgeContent={row.subRows.length} color="primary" />
    </HeaderContainer>
    <ActionsContainer>
      {actions}
      <Icon name={row.getIsExpanded() ? 'expand_less' : 'expand_more'} />
    </ActionsContainer>
  </Content>
);

export interface GroupHeaderRowProps {
  row: TRow<any>;
}

export const GroupRow = ({ row }: GroupHeaderRowProps) => {
  const { table } = useDataProvider();
  const GroupHeaderComponent = getGroupHeader(table, row);

  return (
    <TableRow key={row.id} className="group">
      {isRowSelectionEnabled(table) && <Cell />}
      <Cell
        colSpan={getColSpan(row)}
        onClick={() => row.toggleExpanded(!row.getIsExpanded())}
      >
        <GroupHeaderComponent row={row} />
      </Cell>
    </TableRow>
  );
};
